import { useEffect, useRef, useState } from 'preact/hooks';
import { Modal } from '@/ui/Modal.jsx';
import { notify } from '@/store/ui';
import { extractGtin, toDataMatrixPayload } from '@/utils/marking.js';
import { printLabels } from './printLabels.js';
import css from './MarkingModal.module.css';

/**
 * Коды маркировки «Честный ЗНАК» рисуются в DataMatrix — именно эту символику
 * читают сканеры и приложение системы маркировки. QR-код внешне похож, но не
 * подходит: по нему код в системе не находится.
 *
 * Библиотека штрихкодов подгружается только при открытии окна — она весит
 * заметно больше самого приложения, а нужна далеко не в каждой сессии.
 */
export function MarkingModal({ item, onClose }) {
  const containerRef = useRef(null);
  const codes = item?.marking_codes || [];
  const [state, setState] = useState('loading'); // loading | ready | failed

  useEffect(() => {
    if (codes.length === 0) {
      setState('ready');
      return;
    }

    let cancelled = false;
    setState('loading');

    import('bwip-js/browser')
      .then((bwipjs) => {
        if (cancelled || !containerRef.current) return;

        containerRef.current.querySelectorAll('[data-code]').forEach((el) => {
          const code = el.getAttribute('data-code');
          const payload = toDataMatrixPayload(code);

          if (!payload) {
            el.textContent = 'Код не распознан';
            el.classList.add(css.codeBroken);
            return;
          }

          try {
            const svg = bwipjs.toSVG({
              bcid: 'datamatrix',
              text: payload,
              parsefnc: true, // включает разбор ^FNC1 и ^029 внутри строки
              scale: 4,
              padding: 2,
              backgroundcolor: 'FFFFFF',
            });
            const doc = new DOMParser().parseFromString(svg, 'image/svg+xml');
            el.replaceChildren(doc.documentElement);
          } catch (e) {
            console.warn('DataMatrix render failed', code, e);
            el.textContent = 'Ошибка построения кода';
            el.classList.add(css.codeBroken);
          }
        });

        setState('ready');
      })
      .catch((e) => {
        console.warn('bwip-js load failed', e);
        if (!cancelled) setState('failed');
      });

    return () => {
      cancelled = true;
    };
  }, [codes]);

  async function copy(code) {
    try {
      await navigator.clipboard?.writeText(code);
      notify('Код скопирован');
    } catch {
      notify('Не удалось скопировать код');
    }
  }

  async function copyAll() {
    try {
      await navigator.clipboard?.writeText(codes.map((c) => c.code).join('\n'));
      notify(`Скопировано кодов: ${codes.length}`);
    } catch {
      notify('Не удалось скопировать коды');
    }
  }

  function print() {
    const labels = [];
    containerRef.current?.querySelectorAll('[data-code]').forEach((el) => {
      const svg = el.querySelector('svg');
      if (svg) labels.push({ code: el.getAttribute('data-code'), svg: svg.outerHTML });
    });

    if (labels.length === 0) {
      notify('Нечего печатать: коды ещё не построены');
      return;
    }
    printLabels(item?.name || 'Коды маркировки', labels);
  }

  return (
    <Modal open title={item?.name} size="lg" onClose={onClose}>
      <div ref={containerRef} class={css.root}>
        {codes.length === 0 ? (
          <p class="empty-state">Коды маркировки появятся после отгрузки заказа.</p>
        ) : (
          <>
            <div class={css.toolbar}>
              <p class={css.summary}>
                Всего кодов: <strong>{codes.length}</strong>
              </p>
              <div class={css.actions}>
                <button type="button" class="btn btn-secondary btn-sm" onClick={copyAll}>
                  Скопировать все
                </button>
                <button
                  type="button"
                  class="btn btn-primary btn-sm"
                  onClick={print}
                  disabled={state !== 'ready'}
                >
                  Печать этикеток
                </button>
              </div>
            </div>

            {state === 'failed' && (
              <p class={css.warning}>
                Не удалось загрузить модуль штрихкодов — проверьте связь и обновите страницу.
                Сами коды доступны текстом под каждой позицией.
              </p>
            )}

            <div class={css.grid}>
              {codes.map((c) => (
                <div key={c.code} class={css.item}>
                  <div class={css.barcode} data-code={c.code} />
                  <code class={css.code}>{c.code}</code>
                  {extractGtin(c.code) && <span class={css.gtin}>GTIN {extractGtin(c.code)}</span>}
                  <button
                    type="button"
                    class="btn btn-secondary btn-sm"
                    onClick={() => copy(c.code)}
                  >
                    Копировать
                  </button>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
